/**
 * The site-wide pill vocabulary editor: a pill shows a name with a pencil (rename in place) and a cross
 * (remove); an add box promotes whatever is typed to a pill on Enter; a heading renames in place the
 * same way. Tagging categories, their values and the visual direction lists all share it.
 */
import { useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Icon } from '@/components/icons';

interface InlineEdit { editing: boolean; start: () => void; input: ReactNode }

/** Edit-in-place state: Enter or blur commits a changed, non-empty name; Escape gives it up. */
function useInlineEdit(value: string, onCommit: (next: string) => void, opts: { maxLength: number; className: string; label: string }): InlineEdit {
  const [draft, setDraft] = useState<string | null>(null);
  // The draft's live mirror: the blur that follows Enter (the input unmounts) must not commit twice.
  const live = useRef<string | null>(null);
  const set = (v: string | null) => { live.current = v; setDraft(v); };
  const commit = () => {
    const text = live.current;
    if (text === null) return;
    set(null);
    const next = text.trim();
    if (next && next !== value) onCommit(next);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { e.preventDefault(); set(null); }
  };
  return {
    editing: draft !== null,
    start: () => set(value),
    input: draft === null ? null : (
      <input className={opts.className} value={draft} size={Math.max(draft.length, 4)} maxLength={opts.maxLength} aria-label={opts.label} autoFocus
        onChange={(e) => set(e.target.value)} onKeyDown={onKeyDown} onBlur={commit} />
    ),
  };
}

export function Pill({ label, dim = false, maxLength, onRename, onRemove, removeWarning = null, extra, draggable = false, dragTitle, onDragStart, onDragEnd }: {
  label: string;
  /** A value kept for search but no longer offered. */
  dim?: boolean;
  maxLength: number;
  onRename: (label: string) => void;
  onRemove: () => void;
  /** When set, the cross first turns the pill into this warning with Remove and Keep. */
  removeWarning?: string | null;
  /** One more action between the pencil and the cross. */
  extra?: ReactNode;
  draggable?: boolean;
  dragTitle?: string;
  onDragStart?: (e: DragEvent<HTMLSpanElement>) => void;
  onDragEnd?: () => void;
}) {
  const edit = useInlineEdit(label, onRename, { maxLength, className: 'tg-input', label: `Rename ${label}` });
  const [confirming, setConfirming] = useState(false);
  if (edit.editing) return <span className="tg tg-editing">{edit.input}</span>;
  if (confirming) {
    return (
      <span className="tg tg-confirm" role="alertdialog" aria-label={`Remove ${label}?`}>
        {label}: {removeWarning}
        <button type="button" className="tg-confirm-btn danger" onClick={() => { setConfirming(false); onRemove(); }}>Remove</button>
        <button type="button" className="tg-confirm-btn" autoFocus onClick={() => setConfirming(false)}>Keep</button>
      </span>
    );
  }
  return (
    <span className={`tg${dim ? ' inh' : ''}${draggable ? ' tg-drag' : ''}`} draggable={draggable} title={dragTitle}
      onDragStart={onDragStart} onDragEnd={onDragEnd}>
      {label}
      <span className="tg-acts">
        <button type="button" className="tg-act tg-act-edit" title="Rename" aria-label={`Rename ${label}`} onClick={edit.start}><Icon name="pencil" /></button>
        {extra}
        <button type="button" className="tg-act" title="Remove" aria-label={`Remove ${label}`} onClick={() => (removeWarning ? setConfirming(true) : onRemove())}><Icon name="close" /></button>
      </span>
    </span>
  );
}

/** The add box at the end of a pill row: Enter, or the return button inside the box, promotes the text to a pill. */
export function PillInput({ placeholder, label, maxLength, disabled = false, onAdd }: {
  placeholder: string;
  label: string;
  maxLength: number;
  disabled?: boolean;
  onAdd: (label: string) => void;
}) {
  const [text, setText] = useState('');
  const add = () => {
    const next = text.trim();
    if (!next) return;
    onAdd(next);
    setText('');
  };
  return (
    <span className="tg-add-wrap">
      <input className="tg-add tg-add-roomy" placeholder={placeholder} aria-label={label} value={text} maxLength={maxLength} disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          add();
        }} />
      <button type="button" className="tg-add-go" title="Add (or press Enter)" aria-label={label} disabled={disabled || !text.trim()} onClick={add}>
        <Icon name="enter" />
      </button>
    </span>
  );
}

/** A heading that renames in place behind a pencil. */
export function InlineName({ value, className, maxLength, onRename }: { value: string; className: string; maxLength: number; onRename: (label: string) => void }) {
  const edit = useInlineEdit(value, onRename, { maxLength, className: 'name-input', label: `Rename ${value}` });
  if (edit.editing) return <>{edit.input}</>;
  return (
    <>
      <span className={className}>{value}</span>
      <button type="button" className="icon-btn name-edit" title="Rename" aria-label={`Rename ${value}`} onClick={edit.start}><Icon name="pencil" /></button>
    </>
  );
}

/** A plain list of names (the direction lines) as pills with an add box; the list itself is the state. */
export function PillList({ items, placeholder, label, maxLength, max, onChange }: {
  items: string[];
  placeholder: string;
  label: string;
  maxLength: number;
  max: number;
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="tag-wrap">
      {items.map((line, i) => (
        <Pill key={`${i}:${line}`} label={line} maxLength={maxLength}
          onRename={(next) => onChange(items.map((l, k) => (k === i ? next : l)))}
          onRemove={() => onChange(items.filter((_, k) => k !== i))} />
      ))}
      <PillInput placeholder={items.length >= max ? `Full: at most ${max}` : placeholder} label={label} maxLength={maxLength} disabled={items.length >= max}
        onAdd={(next) => { if (!items.includes(next)) onChange([...items, next]); }} />
    </div>
  );
}
